import {useCallback, useMemo} from 'react'
import {FormAction} from '@komune-io/g2-forms'
import {BasicProps} from '@komune-io/g2-themes'
import {FormComposable, FormComposableField, useFeedback} from "@tracabois/components";
import {useFormComposable} from "@komune-io/g2-composable";
import {DropZonePicture} from "@komune-io/g2-fs";
import { CreditedFile, FileName, ExternalFile, METADATA_CREDIT_KEY, useDropZonePicture, externalFileUrl } from '@tracabois/domain';
import { Box } from '@mui/material';

type CreditedImageFormValue = {
  credit: string,
  toUseAsCover: boolean
}

export interface CreditedImageFormProps extends BasicProps {
  item?: ExternalFile
  isCover?: boolean
  onAdd: (item: CreditedFile, toUseAsCover: boolean) => Promise<boolean> | boolean
  onUpdate: (name: FileName) => Promise<boolean> | boolean
  onDelete: (name: FileName) => Promise<boolean> | boolean
  onCancel: () => void
}

export const CreditedImageForm = (props: CreditedImageFormProps) => {
  const {
    item,
    isCover,
    onAdd,
    onUpdate,
    onDelete,
    onCancel
  } = props
  const submitFeedback = useFeedback()
  const labels = {
    submit: "Enregistrer",
    delete: "Supprimer",
    cancel: "Annuler"
  }

  const imageDropZone = useDropZonePicture(externalFileUrl(item))

  const onSubmitMemoized = useCallback(
    async (value: CreditedImageFormValue) => {
      if ( !item && !imageDropZone.pictureUrl) {
        return false
      }
      const result = imageDropZone.pictureUrl ? await onAdd({
        file: imageDropZone.pictureUrl,
        name: imageDropZone.fileName!!,
        credit: value.credit,
      }, value.toUseAsCover) : await onUpdate(item!.name)
      submitFeedback.setFeedback(result)

      if (result) onCancel()
      return result
    },
    [item, imageDropZone, onAdd, onUpdate]
  )

  const onDeleteClicked = useCallback(
    async () => {
      if (onDelete && item) {
        const result = await onDelete(item.name)
        if (result) onCancel()
        return result
      }
      return false
    },
    [onDelete, item]
  )

  const fields: FormComposableField[] = useMemo(
    (): FormComposableField[] => [
      {
        key: 'credit',
        name: 'credit',
        type: 'textField',
        label: 'Crédit photo',
        readOnly: item !== undefined
      },
      {
        key: 'toUseAsCover',
        name: 'toUseAsCover',
        type: 'checkBox',
        label: 'Utiliser cette image comme photo de couverture',
        readOnly: isCover
      },
    ],
    []
  )

  const formState = useFormComposable<CreditedImageFormValue>({
    onSubmit: onSubmitMemoized,
    formikConfig: {
      initialValues: {
        credit: item?.metadata?.[METADATA_CREDIT_KEY] ?? '',
        toUseAsCover: isCover ?? false,
      }
    },
  })

  const actions: FormAction[] =  useMemo(() =>[{
    key: "cancel",
    label: labels.cancel,
    onClick: onCancel,
    variant: "cancellation"
  }, {
    key: "delete",
    label: labels.delete,
    showIf: () => item != undefined,
    onClick: onDeleteClicked,
    variant: "deletion"
  }, {
    key: "submit",
    label: labels.submit,
    success: submitFeedback.isSuccess,
    fail: submitFeedback.isFailure,
    onClick: formState.submitForm,
    disabled: (!item && !imageDropZone.pictureUrl) || (item && JSON.stringify(formState.values) === JSON.stringify(formState.initialValues))
  }],[onCancel, labels.cancel, labels.delete, labels.submit, submitFeedback, formState, item, imageDropZone])

  return (
    <Box style={{width: '500px'}}>
      <DropZonePicture {...imageDropZone} readOnly={item != undefined}/>
      <FormComposable
        fields={fields}
        formState={formState}
        actions={actions}
      />
    </Box>
  )
}
